import React, { FC } from 'react';
import { Radio, Checkbox } from '../form-fields';
import { DUAL_PERIOD_AUTO_ADVANCE_DELAY_MS } from '../../data/constants';
import useAutoAdvance from '../../hooks/use-auto-advance';
import type { QuestionProps } from './types';

export const DualPeriodQuestion: FC<QuestionProps> = ({
  question, answers, setAnswers, onComplete,
}) => {
  const base = answers[question.id] || {};
  const updateAnswerField = (k: string, v: any) => setAnswers((p) => ({
    ...p,
    [question.id]: { ...(p[question.id] || {}), [k]: v },
  }));

  const { isAutoAdvancing } = useAutoAdvance(
    base.answer === 'No',
    onComplete,
    DUAL_PERIOD_AUTO_ADVANCE_DELAY_MS,
    question.id,
  );

  const handleToggleExample = (side: string, optValue: string) => {
    const cur: string[] = base[side] || [];
    updateAnswerField(side, cur.includes(optValue)
      ? cur.filter((x) => x !== optValue)
      : [...cur, optValue]);
  };

  const adulthood = question.periodOptions?.adulthood;
  const childhood = question.periodOptions?.childhood;
  const current = question.periodOptions?.current;

  // Young DIVA-5 asks about one period ("current"); adult questions keep both columns.
  const periods = current && !adulthood
    ? [
      {
        side: 'current',
        label: current.label || 'Examples',
        opts: current.options || [],
      },
    ]
    : [
      {
        side: 'adult',
        label: adulthood?.label || 'Examples during adulthood',
        opts: adulthood?.options || [],
      },
      {
        side: 'child',
        label: childhood?.label || 'Examples during childhood',
        opts: childhood?.options || [],
      },
    ];
  const gridColumnsClass = periods.length > 1 ? 'sm:grid-cols-2' : '';

  return (
    <div>
      <p className="text-base font-bold text-slate-800 leading-snug mb-4">
        {question.text}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </p>
      <div className="flex gap-6 mb-4">
        {['Yes', 'No'].map((o) => (
          <Radio
            key={o}
            label={o}
            selected={base.answer === o}
            onClick={() => updateAnswerField('answer', o)}
          />
        ))}
      </div>
      {base.answer === 'Yes' && (
        <div className={`grid grid-cols-1 ${gridColumnsClass} gap-4 mt-2`}>
          {periods.map((col) => (
            <div
              key={col.side}
              className="bg-slate-50 border border-slate-200 rounded-xl p-4"
            >
              <p className="text-xs font-bold text-slate-600 mb-3">
                {col.label}
                {!question.examplesOptional && <span className="text-red-500 ml-0.5">*</span>}
              </p>
              <div className="space-y-2 mb-3">
                {col.opts.map((opt) => {
                  const optKey = String(opt.value);
                  return (
                    <Checkbox
                      key={optKey}
                      label={opt.label}
                      selected={(base[col.side] || []).includes(optKey)}
                      onClick={() => handleToggleExample(col.side, optKey)}
                    />
                  );
                })}
              </div>
              <div className="mb-3">
                <p className="text-xs text-slate-500 mb-1">Other:</p>
                <input
                  className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none bg-white"
                  value={base[`${col.side}Other`] || ''}
                  onChange={(e) => updateAnswerField(`${col.side}Other`, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {isAutoAdvancing && (
        <div className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-brand">
          <span className="inline-block w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          Moving to next question…
        </div>
      )}
    </div>
  );
};

export default DualPeriodQuestion;
